import { useEffect, useState } from 'react';
import { T } from '../components/text/TextEdit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PenLine, Sparkles, FileSearch, ArrowLeft } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Skeleton } from './pipeline/ui';

// The door picker, lifted out of the projects page so that page could be deleted. The behaviour is
// the code that ran there - doors, entitlement gate, industry, createProject - MOVED rather than
// rewritten, because rewriting the only path that can create anything is not a thing to do while
// also changing what it does.
//
// WHAT CHANGED: nobody is asked for a name. Ali, 2026-08-03 - "generate the name with the idea and
// never ask". generate-idea writes the project's name from the idea the moment one exists (shipped
// and deployed separately), so the Path A doors create immediately and land on the idea page. The
// placeholder below is never rendered anywhere: the dashboards show the IDEA's name, and a project
// without an idea yet shows an em-dash.
//
// The Review door still asks. A Path B report has no idea to take a name from, and Path B is parked
// under CLAUDE.md §0 - inventing a naming scheme for it here would be designing it.

const DOORS = [
  { key: 'user', cap: 'idea_input', icon: PenLine },
  { key: 'ai', cap: 'idea_suggest', icon: Sparkles },
  { key: 'review', cap: 'path_b', icon: FileSearch },
];

// Never rendered. projects.name is NOT NULL and the row exists before the idea does; the moment
// generate-idea returns, the server overwrites this.
const UNNAMED = '—';

function DoorCard({ door, onPick }) {
  const { t } = useTranslation();
  const Icon = door.icon;
  return (
    <button
      type="button"
      onClick={() => onPick(door.key)}
      data-testid={`door-${door.key}`}
      className="flex items-start gap-3 rounded-md border border-border bg-card p-4 text-start transition-all hover:border-primary hover:shadow-sm"
    >
      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
      <span className="flex flex-col">
        <span className="font-heading font-semibold text-foreground">{t(`dashboard.door_${door.key}_title`)}</span>
        <span className="text-sm leading-snug text-muted-foreground">{t(`dashboard.door_${door.key}_desc`)}</span>
      </span>
    </button>
  );
}

// A1: never render a door until entitlements have RESOLVED, so a gated door cannot flash.
export function DoorPicker({ ready, doors, onPick }) {
  const { t } = useTranslation();
  if (!ready) {
    return (
      <div className="mt-1 grid gap-3 sm:grid-cols-1" data-testid="door-loading">
        <Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" />
      </div>
    );
  }
  if (doors.length === 0) {
    return <p className="py-6 text-center text-sm text-muted-foreground" data-testid="no-doors"><T k="dashboard.noDoors" /></p>;
  }
  return (
    <div className="mt-1 grid gap-3 sm:grid-cols-1" data-testid="door-grid">
      {doors.map((d) => <DoorCard key={d.key} door={d} onPick={onPick} />)}
    </div>
  );
}

export default function NewIdeaDialog({ open, onOpenChange, only = null, caps = null }) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [paths, setPaths] = useState(null);
  const [entReady, setEntReady] = useState(false);
  const [screen, setScreen] = useState('pick');
  const [door, setDoor] = useState(only);
  const [name, setName] = useState('');
  const [industryKey, setIndustryKey] = useState('');
  const [industries, setIndustries] = useState([]);
  const [busy, setBusy] = useState(false);

  // Fall open if the manifest fails - the server still enforces the real gate on every call.
  const doorVisible = (cap) => !paths || paths[cap] === true;
  const visibleDoors = DOORS.filter((d) => (!caps || caps.includes(d.cap)) && doorVisible(d.cap));

  const create = async (which, chosenIndustry) => {
    const isReview = which === 'review';
    if (isReview && name.trim().length < 2) { toast.error(t('dashboard.nameRequired')); return; }
    setBusy(true);
    try {
      const p = await api.createProject(isReview ? name.trim() : UNNAMED, user.id, {
        origin: which,
        industryKey: which === 'ai' && chosenIndustry ? chosenIndustry : null,
      });
      onOpenChange(false);
      navigate(isReview ? `/project/${p.id}/pathb` : `/project/${p.id}`);
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const pickDoor = (key) => {
    setDoor(key);
    // The AI door needs an industry and the Review door needs a name; "suggest by me" needs
    // nothing at all, so it creates on the click rather than showing an empty form.
    if (key === 'user') { create('user'); return; }
    setScreen('form');
    if (key === 'ai' && industries.length === 0) {
      // N-97: one resolver for corpus language. users.language is what generation reads
      // (reconciled on every session by N-98); the raw UI value swapped the corpus outright.
      api.getMe()
        .then((me) => api.listIndustries(me?.language || i18n.language))
        .then((r) => setIndustries(r.industries || []))
        .catch(() => setIndustries([]));
    }
  };

  // Reset and resolve entitlements from the `open` PROP, not from onOpenChange.
  //
  // onOpenChange fires when the DIALOG changes its own state - overlay click, Escape, the close
  // button. It does not fire when a parent sets the prop, which is how the new-idea button opens
  // this. Doing the work there meant getManifest was never called, entReady never became true,
  // and the picker showed its loading skeleton forever. Found by clicking it; every string check
  // in the commit that introduced it passed.
  useEffect(() => {
    if (!open) return;
    setScreen(only && only !== 'user' ? 'form' : 'pick');
    setDoor(only);
    setName('');
    setIndustryKey('');
    setEntReady(false);
    api.getManifest()
      .then((m) => setPaths(m?.entitlements?.paths || null))
      .catch(() => setPaths(null))
      .finally(() => setEntReady(true));
  }, [open, only]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="door-picker-dialog">
        {screen === 'pick' ? (
          <>
            <DialogHeader><DialogTitle className="font-heading"><T k="dashboard.chooseDoor" /></DialogTitle></DialogHeader>
            <DialogDescription><T k="dashboard.chooseDoorSubtitle" /></DialogDescription>
            <DoorPicker ready={entReady} doors={visibleDoors} onPick={pickDoor} />
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-heading">
                {!only && (
                  <button onClick={() => setScreen('pick')} className="text-muted-foreground hover:text-foreground" data-testid="door-form-back">
                    <ArrowLeft className="h-4 w-4" />
                  </button>
                )}
                {t(`dashboard.door_${door}_title`)}
              </DialogTitle>
            </DialogHeader>
            <DialogDescription className="sr-only">{t(`dashboard.door_${door}_desc`)}</DialogDescription>

            <div className="flex flex-col gap-4">
              {door === 'review' && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="new-report-name"><T k="dashboard.modalTitle" /></label>
                  <Input
                    id="new-report-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t('dashboard.namePlaceholder')}
                    onKeyDown={(e) => e.key === 'Enter' && create('review')}
                    data-testid="new-project-name-input"
                    autoFocus
                  />
                </div>
              )}
              {door === 'ai' && (
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-foreground" htmlFor="new-idea-industry"><T k="dashboard.industryLabel" /></label>
                  <Select value={industryKey || '__general__'} onValueChange={(v) => setIndustryKey(v === '__general__' ? '' : v)}>
                    <SelectTrigger id="new-idea-industry" data-testid="new-project-industry-select">
                      <SelectValue placeholder={t('dashboard.industryPlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__general__" data-testid="industry-option-general"><T k="dashboard.industryGeneral" /></SelectItem>
                      {industries.map((ind) => (
                        <SelectItem key={ind.industry_key} value={ind.industry_key} data-testid={`industry-option-${ind.industry_key}`}>{ind.industry_label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground"><T k="dashboard.industryHelp" /></p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button onClick={() => create(door, industryKey)} disabled={busy} data-testid="create-project-confirm">
                {busy ? t('dashboard.creating') : t('dashboard.startProject')}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}